<template>
    <loading v-if="fetchingData"></loading>

    <div v-else-if="can"
         :class="{'relative min-h-900' : !billingDetails}">

        <overlay-locked
            v-if="!billingDetails"
            :confirmButtonText="$t('subscriptions.go_to_billing')"
            :description="$t('subscriptions.need_billing')"
            :showConfirmButton="true"
            confirmButtonLink="settings.billing-details">
        </overlay-locked>

        <div :style="!billingDetails ? `filter: blur(1.5rem);` : ''"
             class="pb-32">

            <settings-heading
                :title="$t('settings.pricing_plan.title')"
                :description="$t('settings.pricing_plan.description')"
            />

            <banner
                v-if="currentCompany.cancel_license"
                :title="$t('settings.pricing_plan.info.title')"
                :description="$t('settings.pricing_plan.info.description', {'date':formattedDate})"
                :link-text="$t('settings.pricing_plan.info.confirmText')"
                :one-line="false"
                inline
                warning
                @click="recoverPlan"
            />

            <pricing-card
                :description="planDescription"
                :frequency="currentCompany.license_frequency"
                :list-items="[$t('settings.pricing_plan.usp_active_vacancy', {'slots':currentCompany.slots || 10}),
                $t('settings.pricing_plan.usp_all_features'),
                $t('settings.pricing_plan.usp_interim'),
                $t('settings.pricing_plan.usp_campaign')]"
                :price="toFixedIfNecessary(currentCompany.price_per_month ? currentCompany.price_per_month : 0)"
                :price-title="priceTitle"
                :title="planTitle"
                with-buttons
                @cancel="startCancellingPlan"
                @edit="startEditingPlan"
                v-if="!noLicense"
            />

            <!-- Updating Plan -->
            <div v-if="editingPlan || noLicense" class="bg-white p-28 mt-32 rounded-6 shadow">
                <h3 class="font-semibold text-heading-2 first-letter:uppercase">
                    {{ noLicense ? $t('settings.pricing_plan.buy_license') : $t('settings.pricing_plan.change_license') }}
                </h3>
                <div class="mt-14 text-primary-80 text-body-sm mb-16">
                    <p>{{ $t('settings.pricing_plan.description_edit_plan') }}</p>
                    <feature v-if="!!currentCompany.legacy_price && !currentCompany.special_price" feature-key="settings_license_pricing_changes_pdf">
                        <p v-html="$t('settings.pricing_plan.description_pricing_changes')"></p>
                    </feature>
                </div>

                <div class="flex items-center w-full rounded-6 bg-white justify-center border border-primary-20">
                    <div class="flex-1 p-16">
                        <p class="block text-body mr-4 mb-8">{{ $t('settings.pricing_plan.new_slot_amount') }}</p>
                        <number-input
                            v-model="form.slots"
                            :min="currentCompany.slot_limit"
                            @input="calculatePrice"
                            :add-disabled="uponRequest"
                        >
                            <template #before="{subtract}">
                                <tooltip
                                    :disabled="form.slots > currentCompany.slot_limit"
                                    position="bottom"
                                >
                                    <template #tooltip>
                                        <p>{{ $t('settings.pricing_plan.tooltip.min_slots', {date_until: slotsLockedUntil}) }}</p>
                                    </template>
                                    <button
                                        role="button"
                                        class="flex items-center px-16 h-full border-r border-primary-20 group-has-[:focus]:border-secondary-80"
                                        :class="[form.slots <= currentCompany.slot_limit || form.slots === 1 ? 'bg-primary-5 cursor-not-allowed text-primary' : 'bg-secondary-80 hover:bg-secondary cursor-pointer text-white']"
                                        :disabled="form.slots <= currentCompany.slot_limit || form.slots === 1"
                                        @click="subtract"
                                    >
                                        -
                                    </button>
                                </tooltip>
                            </template>
                        </number-input>
                    </div>

                    <div v-if="!uponRequest" class="bg-primary-5 py-24 px-32 text-center flex flex-col justify-center rounded-r">
                        <p class="flex items-center">
                            <span class="flex items-center tracking-tight">
                                <span class="text-heading-1"><currency class="font-semibold"/></span>

                                <loading v-if="!newPrice" class="self-center min-h-60 min-w-70" size="small"/>
                                <span v-else class="font-semibold text-big"> {{ slotsAreValid ? newPrice : '?' }}</span>
                            </span>
                            <span class="text-primary-80 ml-4 text-body">/{{ $t('settings.pricing_plan.card.month') }}</span>
                        </p>
                    </div>
                    <div v-if="uponRequest" class="bg-primary-5 py-20 px-20 text-center flex flex-col justify-center rounded-r max-w-lg">
                        <p class="font-semibold ml-4 text-heading-1">{{ $t('settings.pricing_plan.upon_request') }}</p>
                        <p class="text-balance" v-html="$t('settings.pricing_plan.contact_us')"></p>
                    </div>

                </div>
                <div :class="currentCompany.license_frequency === 'monthly' ? 'justify-between': 'justify-end'"
                     class="flex items-center justify-between mt-32">
                    <toggle v-model="isYearly"
                            :value="form.license_frequency"
                    >
                        <span>{{ $t('settings.pricing_plan.billed_annually') }}</span>

                        <span class="bg-secondary px-8 ml-4 py-4 text-white text-body-sm rounded-6 shadow-sm">-10%</span>
                    </toggle>

                    <simple-button :disabled="currentCompany.active ? !slotsAreValid || uponRequest : uponRequest"
                                   :display="saveDisplay"
                                   :loading="performingUpdateRequest"
                                   @click="updatePlan">
                        {{ noLicense ? $t('settings.pricing_plan.buy') : saveText }}
                    </simple-button>
                </div>
            </div>

            <pricing-card
                v-else-if="currentCompany.downgrade_to || currentCompany.update_license_frequency"
                :description="$t('settings.pricing_plan.card.license_downgrade', {'date':formattedDate})"
                :frequency="currentCompany.update_license_frequency || currentCompany.license_frequency"
                :list-items="[$t('settings.pricing_plan.usp_active_vacancy', {'slots':currentCompany.downgrade_to || currentCompany.slots}),
                $t('settings.pricing_plan.usp_all_features'),
                $t('settings.pricing_plan.usp_interim'),
                $t('settings.pricing_plan.usp_campaign')]"
                :price="currentCompany.downgrade_price_per_month ? toFixedIfNecessary(currentCompany.downgrade_price_per_month) : null"
                :price-title="$t('settings.pricing_plan.card.new_price')"
                :title="$t('settings.pricing_plan.change_in_license')"
                class="mt-32"
            />

            <p class="mt-48 text-primary-80">{{ $t('general.prices_excl_vat', { vat_label: vatLabelForRegion }) }}</p>
            <p class="mt-4 text-primary-80" v-html="$t('general.processor_agreement_and_terms')"></p>

            <popup-modal-confirm v-if="cancellingPlan"
                                 :message="$t('settings.pricing_plan.popup_license_stop')"
                                 :title="$t('settings.pricing_plan.popup_license_stop_title')"
                                 :working="performingUpdateRequest"
                                 @close="stopCancellingPlan"
                                 @confirm="cancelPlan">
                <p class="text-body-sm mt-32 mb-16">
                    <span class="block font-semibold">{{ $t('settings.pricing_plan.popup_license_title') }}</span>
                    {{ $t('settings.pricing_plan.popup_license_info') }}
                </p>
                <select-dropdown
                    class="mb-16 w-1/2"
                    v-model="cancellationReason"
                    :options="cancellationReasons"/>

                <simple-textarea
                    v-model="cancellationInfo"
                    class="mb-16"
                >
                    {{ $t('applications.feedback_label') }}
                </simple-textarea>
            </popup-modal-confirm>

            <popup-modal-confirm v-if="confirmData" @close="confirmData = null"
                                 @confirm="updatePlan(null, true)" confirm-button="success"
                                 :title="$t('settings.pricing_plan.confirmation.title')"
                                 :message="$t('settings.pricing_plan.confirmation.description')"
                                 :confirm-text="$t('settings.pricing_plan.confirmation.confirm')"
                                 :working="performingUpdateRequest"
                                 :toggle-text="$t('settings.pricing_plan.confirmation.toggle')">
                <invoice-confirmation-details :title="confirmData.title" :price="confirmData.price"/>
            </popup-modal-confirm>
        </div>
    </div>
    <error-403 v-else></error-403>
</template>

<script>
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useAuthenticationStore} from '../../state/modules/authentication';
import {useNotificationsStore} from '../../state/modules/notifications';
import Error403 from '../../components/ui/Error403.vue';
import styles from '../../../../../../styles.config';
import RangeSlider from 'vue-range-slider';
import 'vue-range-slider/dist/vue-range-slider.css';
import Checkbox from '../../components/forms/Checkbox';
import SimpleButton from '../../components/ui/SimpleButton';
import Form from '../../etc/forms/Form';
import Tooltip from '../../components/navigation/Tooltip';
import moment from 'moment';
import * as companies from '../../services/CompanyService';
import SimpleInput from '../../components/forms/Input';
import SvgIcon from '../../components/ui/SvgIcon';
import Company from '../../models/Company';
import {normalizeJson} from '../../../shared/helpers';
import PopupModalConfirm from '../../components/ui/PopupModalConfirm';
import PricingCard from '../../components/PricingCard';
import Loading from '../../components/ui/Loading';
import OverlayLocked from '../../components/ui/OverlayLocked';
import BillingDetails from '../../models/BillingDetails';
import * as billingDetailsService from '../../services/BillingDetailsService';
import Toggle from '../../components/forms/Toggle';
import InvoiceConfirmationDetails from "../../components/InvoiceConfirmationDetails";
import SelectDropdown from "../../components/forms/SelectDropdown.vue";
import TextArea from "../../components/forms/TextArea.vue";
import RegionLabel from "../../mixins/regionLabel";
import Currency from '../../components/Currency';
import Feature from "../../components/Feature.vue";
import Banner from "../../components/ui/Banner.vue";
import NumberInput from "../../components/ui/NumberInput.vue";
import SettingsHeading from "../../components/ui/SettingsHeading.vue";

const DEFAULT_SLOT_SIZE = 1;

export default {
    components: {
        SettingsHeading,
        NumberInput,
        Banner,
        Feature,
        InvoiceConfirmationDetails,
        Loading,
        PricingCard,
        PopupModalConfirm,
        SvgIcon,
        Tooltip,
        SimpleButton,
        Checkbox,
        RangeSlider,
        Error403,
        SimpleInput,
        OverlayLocked,
        Toggle,
        SelectDropdown,
        'simple-textarea': TextArea,
		Currency,
    },
    mixins: [
        RegionLabel,
    ],
    data: function () {
        return {
            form: null,
            newPrice: null,
            uponRequest: false,
            editingPlan: false,
            showCustomAmount: false,
            performingUpdateRequest: false,
            cancellingPlan: false,
            isYearly: true,
            cancellationReason: null,
            cancellationInfo: null,
            fetchingData: false,
            confirmData: null,
        };
    },
    computed: {
        ...mapState(useMainStore, ['locale', 'dateFormat', 'currentCompany']),
        ...mapState(useAuthenticationStore, ['currentCompanyId']),
        colors() {
            return styles.colors;
        },
        can() {
            return this.$can('settings.finance');
        },
        cancellationReasons() {
            return [
                {value: 1, label: this.$t('license.cancellation_reason_1')},
                {value: 2, label: this.$t('license.cancellation_reason_2')},
                {value: 3, label: this.$t('license.cancellation_reason_3')},
                {value: 4, label: this.$t('license.cancellation_reason_4')},
            ];
        },
        planTitle() {
            if (this.currentCompany.legacy_price) {
                return this.$t('settings.pricing_plan.card.title_grandfather');
            }
            if (this.currentCompany.free_trial) {
                return this.$t('settings.pricing_plan.card.title_trial');
            }

            return this.$t('settings.pricing_plan.card.title');
        },
        planDescription() {
            if (this.currentCompany.active) {
                let type = this.$t('settings.pricing_plan.license');

                if (this.currentCompany.free_trial) {
                    type = this.$t('settings.pricing_plan.card.free_trial');
                }

                return this.$t('settings.pricing_plan.card.license_ending', {'type': type, 'date': this.formattedDate});

            } else {
                return this.$t('settings.pricing_plan.card.description', {'slots': this.currentCompany.slots});
            }
        },
        priceTitle() {
            if (this.currentCompany.free_trial) {
                return this.$t('settings.pricing_plan.card.free_trial');
            }

            return this.$t('settings.pricing_plan.card.current_plan');
        },
        saveText() {
            if (this.form.slots < this.currentCompany.slots) {
                return this.$t('settings.pricing_plan.downgrade');
            } else if (this.form.slots > this.currentCompany.slots) {
                return this.$t('settings.pricing_plan.upgrade');
            } else {
                return this.$t('settings.pricing_plan.save');
            }
        },
        saveDisplay() {
            if (this.form.slots < this.currentCompany.slots) {
                return 'primary';
            } else if (this.form.slots > this.currentCompany.slots) {
                return 'secondary';
            } else {
                return 'primary';
            }
        },
        formattedDate() {
            let date = this.currentCompany.license_expires_at;

            if (this.currentCompany.in_trial) {
                date = this.currentCompany.trial_end;
            }

            return moment(date).locale(this.locale).format(this.dateFormat);
        },
        slotsAreValid() {
            const slots = parseInt(this.form.slots);
            return !isNaN(slots) && !(slots < this.defaultSlotSize);
        },
        //Check if a company has filled in the billing details
        billingDetails() {
            return useRepo(BillingDetails).where('company_id', this.currentCompanyId).first();
        },
        noLicense() {
            return !(this.currentCompany.active || this.currentCompany.slots)
        },
        defaultSlotSize() {
            if (this.currentCompany.slot_limit) {
                return Math.min(this.currentCompany.slot_limit, this.currentCompany.slots);
            } else {
                return DEFAULT_SLOT_SIZE;
            }
        },
        slotsLockedUntil() {
            return moment(this.currentCompany.locked_slots_until).locale(this.locale).format('LL');
        }
    },
    methods: {
        generateForm() {
            this.newPrice = this.currentCompany.downgrade_price_per_month;

            return new Form([
                {name: 'slots', value: this.currentCompany.downgrade_to || this.currentCompany.slots || 5},
                {
                    name: 'license_frequency',
                    value: this.currentCompany.update_license_frequency || this.currentCompany.license_frequency || 'yearly',
                },
            ]);
        },
        startEditingPlan() {
            this.editingPlan = true;
        },
        startCancellingPlan() {
            this.cancellingPlan = true;
        },
        stopCancellingPlan() {
            this.cancellingPlan = false;
        },
        cancelPlan() {
            this.updatePlan({
                cancel_license: true,
                cancel_reason: this.cancellationReasons.find(reason => reason.value === this.cancellationReason).label,
                cancel_info: this.cancellationInfo
            });
        },
        recoverPlan() {
            this.updatePlan({
                cancel_license: false,
            });
        },
        updatePlan(overrideData, confirmed) {
            this.performingUpdateRequest = true;

            this.form.submit(data => {
                return companies.updatePlan(this.currentCompany.id, {
                    confirmed,
                    data: {
                        attributes: overrideData || data,
                    },
                });
            }).then((data) => {
                if (data.confirm) {
                    this.confirmData = data;
                } else {
                    this.confirmData = null;
                    return useRepo(Company).save(normalizeJson(data));
                }
            }).then(result => {
                this.editingPlan = false;
                this.stopCancellingPlan();
            }).finally(() => this.performingUpdateRequest = false);
        },
        onSlotsChange() {
            if (!this.slotsAreValid) {
                this.form.slots = this.defaultSlotSize;
            }
        },
        subtractOne() {
            if (isNaN(parseInt(this.form.slots))) {
                this.form.slots = this.defaultSlotSize;
            }
            this.form.slots--;
            this.onSlotsChange();
            this.calculatePrice();
        },
        addOne() {
            if (isNaN(parseInt(this.form.slots))) {
                this.form.slots = 0;
            }
            this.form.slots++;
            this.onSlotsChange();
            this.calculatePrice();
        },
        calculatePrice() {
            this.newPrice = this.uponRequest ? this.newPrice : null;

            this.onSlotsChange();

            companies.price(this.currentCompany.id, this.form.slots, this.form.license_frequency)
                .then(price => {
                    this.uponRequest = price === 'request'
                    this.newPrice = price
                });
        },
        toFixedIfNecessary(value) {
            return +parseFloat(value).toFixed(2);
        },
    },
    watch: {
        isYearly() {
            this.form.license_frequency = (this.isYearly ? 'yearly' : 'monthly');
            this.calculatePrice();
        },
    },
    mounted() {
        this.form = this.generateForm();
        this.calculatePrice();

        if (!this.currentCompany.has_license) {
            this.startEditingPlan();
        }

        if (this.currentCompany.update_license_frequency === 'monthly' || this.currentCompany.license_frequency === 'monthly') {
            this.isYearly = false;
        }

        if (!this.billingDetails) {
            this.fetchingData = true;

            billingDetailsService.show(this.currentCompanyId).then(result => {
                if (result) {
                    return useRepo(BillingDetails).save(normalizeJson(result));
                }
            }).finally(() => {
                this.fetchingData = false;
            });
        }
    },
};
</script>

<style scoped>
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Firefox */
input[type=number] {
    -moz-appearance: textfield;
    text-align: center;
}
</style>
